// Pantalla Políticas (Admin SureKeep) — port fiel del mock portal-admin-sk-politicas.html:
// 3 tabs (Estándar con barra SCI + planes, Especiales con palancas/alcance/vigencia, Auditoría).
// Las tres leen y escriben loan-offerings; Especiales usa `/store-policies`, con su propio RBAC.
// Los tabs quedan montados (hidden) para no perder borradores al cambiar de tab, como el mock.
import { useMemo, useState } from 'react';
import { ErrorState, Loading } from '@/kit';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { mxDay } from '@/lib/dateHelpers';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { PageTabs } from '@/shared/components/PageTabs';
import { resolveSegmentsAbilities } from '../segments/permissions';
import { useCreditPlansLog, usePolicies } from './api/usePolicies';
import { readFailureCopy } from './logic';
import { POLICIES_PERMISSIONS, resolveStorePolicyAbilities } from './permissions';
import { AuditTab } from './components/AuditTab';
import { SpecialPoliciesTab } from './components/SpecialPoliciesTab';
import { StandardPoliciesTab } from './components/StandardPoliciesTab';

const TABS = [
    { key: 'standard', label: 'Políticas Estándar' },
    { key: 'special', label: 'Políticas Especiales' },
    { key: 'audit', label: 'Auditoría' },
] as const;

export function PoliciesPage() {
    const { permissions } = useAuth();
    // El catálogo de segmentos es de OTRO dominio: `loan.creditPlan.read` no autoriza a leerlo.
    const segmentAbilities = useMemo(() => resolveSegmentsAbilities(permissions), [permissions]);
    const {
        plans,
        engineCuts,
        segments,
        segmentCatalogState,
        isMock,
        plansForbidden,
        plansError,
        isLoading,
    } = usePolicies(segmentAbilities.canRead);
    // El día de referencia se fija al montar: recalcularlo en cada render movería la vigencia bajo
    // el cursor del operador. Lo comparten la vigencia de Especiales y el tope de la Auditoría.
    const today = useMemo(() => mxDay(new Date().toISOString()), []);
    // El día del feed vive acá y no en el hook: es estado de navegación de la tab Auditoría, y el
    // hook tiene que poder pedir cualquier partición, no solo la de hoy.
    const [logDay, setLogDay] = useState(today);
    // Query aparte: la bitácora no entra en el `isLoading`/`isMock` de los planes, o su carga
    // bloquearía la tab Estándar y su fallo sellaría la pantalla entera.
    const auditLog = useCreditPlansLog(logDay);
    const storePolicyAbilities = useMemo(
        () => resolveStorePolicyAbilities(permissions),
        [permissions],
    );
    const [tab, setTab] = useState<string>('standard');
    // Especiales se monta la PRIMERA vez que se abre y ya no se desmonta: así no dispara su
    // listado ni el padrón de tiendas y productos a quien solo vino a mirar la tab Estándar.
    const [visitedSpecial, setVisitedSpecial] = useState(false);

    const panel = (key: string): string =>
        cn('min-h-0 flex-1 flex-col', tab === key ? 'flex' : 'hidden');

    // Primera carga: spinner, NUNCA el mock. Así los planes no parpadean de mock a real en cada
    // reload — el catálogo mock solo aparece si la query FALLA (isMock), con el sello "sin backend".
    if (isLoading) {
        return <Loading fullScreen text="Cargando políticas…" />;
    }

    // Un 403 no es «sin backend»: el sello viejo acusaba de no construido lo que en realidad era
    // un permiso, y detrás dejaba 3 planes falsos y editables. Sin el permiso no hay pantalla.
    if (plansForbidden) {
        const copy = readFailureCopy(plansError, 'las políticas', POLICIES_PERMISSIONS.read);
        return <ErrorState className="m-6" title={copy.title} description={copy.description} />;
    }

    return (
        <MockBoundary active={isMock} className="-mx-5 -my-4 flex h-[calc(100%+2rem)] flex-col">
            <PageTabs
                tabs={TABS}
                active={tab}
                onChange={(key) => {
                    if (key === 'special') setVisitedSpecial(true);
                    setTab(key);
                }}
            />
            <div className={panel('standard')}>
                <StandardPoliciesTab plans={plans} engineCuts={engineCuts} />
            </div>
            <div className={panel('special')}>
                {visitedSpecial ? (
                    <SpecialPoliciesTab
                        abilities={storePolicyAbilities}
                        segments={segments}
                        segmentCatalogState={segmentCatalogState}
                        today={today}
                    />
                ) : null}
            </div>
            <div className={panel('audit')}>
                <AuditTab
                    entries={auditLog.entries}
                    day={logDay}
                    onDayChange={setLogDay}
                    today={today}
                    hasMore={auditLog.hasMore}
                    onLoadMore={auditLog.loadMore}
                    isLoadingMore={auditLog.isLoadingMore}
                    isLoading={auditLog.isLoading}
                    error={auditLog.error}
                    onRetry={auditLog.refetch}
                />
            </div>
        </MockBoundary>
    );
}
